import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useDocsStore } from './store/docsStore';
import { useConfigStore } from './store/configStore';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { applyThemeColors } from './utils/colorUtils';
import { StorageWarning } from './components/Common/StorageWarning';
import { Home } from './pages/Home';
import { DocumentPage } from './pages/DocumentPage';
import { NewDocPage } from './pages/NewDocPage';
import { EditPage } from './pages/EditPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AdminPage } from './pages/AdminPage';
import { MarkdownCheatsheetPage } from './pages/MarkdownCheatsheetPage';
import FlowchartsPage from './pages/FlowchartsPage';
import FlowchartNewPage from './pages/FlowchartNewPage';
import FlowchartEditPage from './pages/FlowchartEditPage';
import FlowchartViewPage from './pages/FlowchartViewPage';
import './styles/globals.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

function App() {
  const checkAuth = useAuthStore((state) => state.checkAuth);
  const initialize = useDocsStore((state) => state.initialize);
  const { config, fetchConfig } = useConfigStore();
  
  // Setup online/offline listeners
  useOnlineStatus();

  // Initialize app: fetch config, check auth, and load docs
  useEffect(() => {
    fetchConfig();
    checkAuth();
    initialize(); // Load cache and sync documents
  }, []);

  // Apply theme colors when accent color changes
  useEffect(() => {
    if (config.accentColor) {
      applyThemeColors(config.accentColor);
    }
  }, [config.accentColor]);

  // Update document title when appTitle changes
  useEffect(() => {
    if (config.appTitle) {
      document.title = config.appTitle;
    }
  }, [config.appTitle]);

  return (
    <BrowserRouter>
      <StorageWarning />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/docs/:slug" element={<DocumentPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        
        <Route
          path="/new"
          element={
            <ProtectedRoute>
              <NewDocPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit/:id"
          element={
            <ProtectedRoute>
              <EditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/markdown-cheatsheet"
          element={
            <ProtectedRoute>
              <MarkdownCheatsheetPage />
            </ProtectedRoute>
          }
        />
        
        {/* Flowchart Routes */}
        <Route
          path="/flowcharts"
          element={
            <ProtectedRoute>
              <FlowchartsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flowcharts/new"
          element={
            <ProtectedRoute>
              <FlowchartNewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flowcharts/edit/:slug"
          element={
            <ProtectedRoute>
              <FlowchartEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flowcharts/:slug"
          element={
            <ProtectedRoute>
              <FlowchartViewPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
